import React, { useState, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Input, InputGroup, InputLeftElement, VStack, HStack,
  Box, Text, Button, Icon, Spinner, Avatar, Alert, AlertIcon, AlertDescription
} from '@chakra-ui/react';
import { FiSearch, FiExternalLink } from 'react-icons/fi';
import { searchCustomers } from 'services/unifiedCustomersSearch';

const Pill = ({ bg, color, children, ...rest }) => (
  <Box as="span" display="inline-flex" alignItems="center" px="8px" py="2px"
    borderRadius="999px" bg={bg} color={color} fontSize="10px" fontWeight="700" {...rest}>
    {children}
  </Box>
);

const ROLE_PILL = {
  customer:   { bg: 'mint.bg', color: 'mint.ink', label: 'Activo' },
  becado:     { bg: 'lav.bg',  color: 'lav.ink',  label: 'Becado' },
  cobranza:   { bg: 'rose.bg', color: 'rose.ink', label: 'Cobranza' },
  subscriber: { bg: 'cream.bg2', color: 'ink.400', label: 'Archivado' },
};

function NewAppointmentModal({ isOpen, onClose, teacherId }) {
  const [searchTerm, setSearchTerm]         = useState('');
  const [results, setResults]               = useState([]);
  const [searching, setSearching]           = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const debounceRef = useRef(null);

  // ── Búsqueda con debounce 300ms ────────────────────────────────────────────
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setSelectedStudent(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!term.trim() || term.trim().length < 2) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await searchCustomers(term.trim(), 15);
        setResults(found);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setResults([]);
    setSearchTerm(
      [student.first_name, student.last_name].filter(Boolean).join(' ') || student.name
    );
  };

  // ── Abrir página de reserva en nueva pestaña con alumno pre-cargado ────────
  const handleOpenBooking = () => {
    if (!selectedStudent?.username || !teacherId) return;
    const url = `${window.location.origin}/#/public/reservar-hora/${teacherId}?student_rut=${encodeURIComponent(selectedStudent.username)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleClose = () => {
    setSearchTerm('');
    setResults([]);
    setSelectedStudent(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
        <ModalHeader borderBottom="1px solid" borderColor="cream.line" color="ink.900" fontWeight="800">
          Nueva Cita
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" color="ink.400">
              Busca al alumno para pre-cargarlo en el formulario — el paso de validación
              de RUT se saltará automáticamente.
            </Text>

            {/* Buscador */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                {searching
                  ? <Spinner size="xs" color="coral.500" />
                  : <Icon as={FiSearch} color="ink.400" boxSize="14px" />}
              </InputLeftElement>
              <Input
                placeholder="Nombre, apellido o RUT del alumno..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
                bg="white"
                borderColor="cream.line"
                focusBorderColor="coral.500"
                size="md"
              />
            </InputGroup>

            {/* Lista de resultados */}
            {results.length > 0 && !selectedStudent && (
              <VStack
                spacing={0}
                align="stretch"
                bg="white"
                border="1px solid"
                borderColor="cream.line"
                borderRadius="12px"
                overflow="hidden"
                maxH="260px"
                overflowY="auto"
              >
                {results.map((student, idx) => {
                  const rolePill = ROLE_PILL[student.role] || { bg: 'cream.bg2', color: 'ink.400', label: student.role };
                  return (
                    <Box
                      key={student.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'coral.tint' }}
                      borderBottom={idx < results.length - 1 ? '1px solid' : 'none'}
                      borderColor="cream.line"
                      onClick={() => handleSelectStudent(student)}
                    >
                      <HStack spacing={3}>
                        <Avatar
                          size="xs"
                          name={[student.first_name, student.last_name].filter(Boolean).join(' ')}
                        />
                        <Box flex="1" minW={0}>
                          <Text fontSize="sm" fontWeight="600" color="ink.900" noOfLines={1}>
                            {student.first_name} {student.last_name}
                          </Text>
                          <Text fontSize="xs" color="ink.400" noOfLines={1}>
                            {student.username}
                            {student.group ? ` · ${student.group}` : ''}
                          </Text>
                        </Box>
                        <Pill bg={rolePill.bg} color={rolePill.color} flexShrink={0}>
                          {rolePill.label}
                        </Pill>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}

            {/* Sin resultados */}
            {!searching && searchTerm.trim().length >= 2 && results.length === 0 && !selectedStudent && (
              <Text fontSize="xs" color="ink.400" textAlign="center" py={2}>
                No se encontraron alumnos con ese criterio.
              </Text>
            )}

            {/* Alumno seleccionado */}
            {selectedStudent && (
              <Box bg="coral.tint" border="1px solid" borderColor="coral.soft" borderRadius="12px" p={3}>
                <Text fontSize="11px" fontWeight="800" color="coral.700" textTransform="uppercase"
                  letterSpacing="0.06em" mb={2}>
                  Alumno seleccionado
                </Text>
                <HStack spacing={3} align="flex-start">
                  <Avatar
                    size="sm"
                    name={[selectedStudent.first_name, selectedStudent.last_name].filter(Boolean).join(' ')}
                  />
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="700" color="ink.900" noOfLines={1}>
                      {selectedStudent.first_name} {selectedStudent.last_name}
                    </Text>
                    <Text fontSize="xs" color="ink.700">
                      RUT: {selectedStudent.username}
                    </Text>
                    {selectedStudent.group && (
                      <Pill bg="white" color="coral.700" mt="4px">
                        {selectedStudent.group}
                      </Pill>
                    )}
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="coral.700"
                    _hover={{ bg: 'coral.soft' }}
                    flexShrink={0}
                    onClick={() => { setSelectedStudent(null); setSearchTerm(''); }}
                  >
                    Cambiar
                  </Button>
                </HStack>
              </Box>
            )}

            {/* Aviso si no hay teacherId */}
            {!teacherId && (
              <Alert status="warning" bg="warn.bg" borderRadius="10px" fontSize="sm">
                <AlertIcon color="warn.500" />
                <AlertDescription color="ink.900">
                  No hay un ID de profesor configurado. Asegúrese de estar logueado como profesor.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="cream.line" gap={2}>
          <Button variant="ghost" size="sm" color="ink.700" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            bg="coral.500"
            color="white"
            fontWeight="700"
            borderRadius="10px"
            isDisabled={!selectedStudent || !teacherId}
            rightIcon={<Icon as={FiExternalLink} boxSize="13px" />}
            _hover={{ bg: 'coral.600' }}
            _active={{ bg: 'coral.700' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            onClick={handleOpenBooking}
          >
            Abrir formulario de reserva
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default NewAppointmentModal;
